/**
 * Issue #207 — 质检报告聚合页高级搜索 / 列隐藏 / 分页的纯逻辑（便于单测，无 React 依赖）。
 *
 * 红线：空串 / NaN / 非法日期一律不下发查询参数（避免给后端塞 NaN / 空过滤）；
 * 列隐藏只在白名单内切换；分页参数始终随查询下发。
 */
import type { QcReportsListQuery } from './types';

/** 高级搜索草稿表单态（点「搜索」后才提交为 applied）。 */
export interface ReportsSearchDraft {
  keyword: string;
  startDate: string;
  endDate: string;
  minScore: string;
  maxScore: string;
  qcResult: string;
  processingStatus: string;
  reviewStatus: string;
}

export const EMPTY_REPORTS_DRAFT: ReportsSearchDraft = {
  keyword: '',
  startDate: '',
  endDate: '',
  minScore: '',
  maxScore: '',
  qcResult: '',
  processingStatus: '',
  reviewStatus: '',
};

function numOrUndefined(v: string): number | undefined {
  if (v.trim() === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/** YYYY-MM-DD（含可选时间）→ ISO；非法 / 空一律返回 undefined。 */
function isoOrUndefined(date: string, endOfDay = false): string | undefined {
  if (!date.trim()) return undefined;
  const d = new Date(`${date}T${endOfDay ? '23:59:59' : '00:00:00'}`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** 把草稿表单 + 分页转换成后端 /qc/reports 查询（空值不下发）。 */
export function toReportsQuery(
  draft: ReportsSearchDraft,
  page: number,
  limit: number,
): QcReportsListQuery {
  return {
    page,
    limit,
    keyword: draft.keyword.trim() || undefined,
    startDate: isoOrUndefined(draft.startDate, false),
    endDate: isoOrUndefined(draft.endDate, true),
    minScore: numOrUndefined(draft.minScore),
    maxScore: numOrUndefined(draft.maxScore),
    qcResult: draft.qcResult || undefined,
    processingStatus: draft.processingStatus || undefined,
    reviewStatus: draft.reviewStatus || undefined,
  };
}

/** 在隐藏集合里切换某列（不可变返回）。 */
export function toggleHiddenColumn(hidden: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(hidden);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** 计算可见列（保持白名单顺序，过滤掉隐藏列）。 */
export function visibleColumns(all: readonly string[], hidden: ReadonlySet<string>): string[] {
  return all.filter((c) => !hidden.has(c));
}
